import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, CheckCircle2, X, Plus, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useSessionId() {
  const ref = useRef(null);
  if (!ref.current) ref.current = `ref-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return ref.current;
}

const SEED_TURN = {
  message: "What kind of news would you like each morning? Pick a topic to start, or enter a topic in your own words and we'll hone it to a fine edge together.",
  suggestions: ["AI news", "Climate & energy", "Startup & VC", "Geopolitics", "Cybersecurity", "Space & science"],
  refined_prompt: "",
  locked: false,
  multi_select: false, // first turn: single-select only
};

export default function RefineChat({ onLock, locked }) {
  const sessionId = useSessionId();
  const [history, setHistory] = useState([]); // [{role, content}]
  const [turnCount, setTurnCount] = useState(0); // counts user-sent turns
  const [input, setInput] = useState("");
  const [selected, setSelected] = useState([]); // multi-select chips for current turn
  const [loading, setLoading] = useState(false);
  const [turn, setTurn] = useState(SEED_TURN);
  const [refined, setRefined] = useState("");
  const [seedLabel, setSeedLabel] = useState(""); // top-level topic chosen on the seed turn ("AI news", "Cybersecurity", etc.)
  const [committedTopics, setCommittedTopics] = useState([]); // [{label, prompt}]
  const [extraTopics, setExtraTopics] = useState([]); // quick-fire simple topics, up to 3
  const [extraInput, setExtraInput] = useState("");
  const scrollRef = useRef(null);
  const extraInputRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, turn]);

  const sendMessage = async (content) => {
    if (!content || loading || locked) return;
    const newHist = [...history];
    if (turn?.message) newHist.push({ role: "assistant", content: turn.message });
    newHist.push({ role: "user", content });
    setHistory(newHist);
    // Capture the top-level topic label on the very first user message
    if (!seedLabel && newHist.filter((h) => h.role === "user").length === 1) {
      setSeedLabel(content.length > 40 ? content.slice(0, 40) + "…" : content);
    }
    setInput("");
    setSelected([]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/news/refine`, {
        session_id: sessionId,
        message: content,
        history: newHist.slice(0, -1),
      });
      setTurn({ ...r.data, multi_select: true });
      setTurnCount((c) => c + 1);
      if (r.data.refined_prompt) setRefined(r.data.refined_prompt);
      if (r.data.locked && r.data.refined_prompt) finalizeAndLock(r.data.refined_prompt);
    } catch {
      toast.error("Couldn't reach the refinement assistant. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendCurrent = () => {
    if (loading || locked) return;
    const parts = [];
    if (selected.length) parts.push(selected.join(", "));
    if (input.trim()) parts.push(input.trim());
    const combined = parts.join(". ").trim();
    if (!combined) return; // no work to send
    sendMessage(combined);
  };

  const toggleChip = (s) => {
    // On the first (seed) turn, chips behave as single-select-and-send for
    // fastest onboarding. On subsequent turns, they're multi-select toggles.
    if (!turn.multi_select) {
      sendMessage(s);
      return;
    }
    setSelected((xs) => (xs.includes(s) ? xs.filter((x) => x !== s) : [...xs, s]));
  };

  // Build a string representing the CURRENT in-progress topic from whatever
  // state is available, falling back to the most-recent user message in
  // history if Claude hasn't yet produced a refined prompt.
  const buildCurrentTopic = () => {
    const parts = [];
    if (refined) parts.push(refined);
    if (selected.length) parts.push(selected.join(", "));
    if (input.trim()) parts.push(input.trim());
    if (parts.length) return parts.join(". ").trim();
    // Fallback: most recent user message in chat history (e.g. a seed chip
    // click that hasn't been refined further yet)
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === "user") return (history[i].content || "").trim();
    }
    return "";
  };

  const finalizeAndLock = (basePrompt) => {
    // Gather all committed topics (each is {label, prompt})
    const topicsStructured = committedTopics.map((t) => ({ ...t }));

    // Current in-progress topic from this chat session
    const current = (basePrompt || "").trim() || buildCurrentTopic();
    if (current && !committedTopics.some((t) => t.prompt === current)) {
      topicsStructured.push({
        label: seedLabel || current.slice(0, 40),
        prompt: current,
      });
    }

    // Extra quick-fire topics — each becomes its own labeled topic
    for (const t of extraTopics) {
      const tt = (t || "").trim();
      if (!tt) continue;
      if (!topicsStructured.some((x) => x.prompt === tt)) {
        topicsStructured.push({ label: tt.length > 40 ? tt.slice(0, 40) + "…" : tt, prompt: tt });
      }
    }

    const finalPrompt = topicsStructured.map((t) => t.prompt).join(". ").trim();
    onLock?.(finalPrompt, topicsStructured);
  };

  const lockNow = () => {
    if (committedTopics.length === 0 && !buildCurrentTopic()) {
      toast.error("Pick a topic or tell the assistant what you care about first.");
      return;
    }
    finalizeAndLock();
  };

  const startAnotherTopic = () => {
    const current = buildCurrentTopic();
    if (!current && history.length === 0) {
      toast.error("Refine a topic first, then add another.");
      return;
    }
    if (current && !committedTopics.some((t) => t.prompt === current)) {
      setCommittedTopics((xs) => [...xs, { label: seedLabel || current.slice(0, 40), prompt: current }]);
    }
    // Fresh chat state
    setHistory([]);
    setTurn(SEED_TURN);
    setTurnCount(0);
    setInput("");
    setSelected([]);
    setRefined("");
    setSeedLabel("");
    toast.success("Topic saved. Start refining your next one.");
    setTimeout(() => {
      scrollRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    }, 50);
  };

  const removeCommittedTopic = (i) => {
    setCommittedTopics((xs) => xs.filter((_, j) => j !== i));
  };

  const addExtraTopic = () => {
    const t = extraInput.trim();
    if (!t) return;
    if (extraTopics.length >= 3) {
      toast.error("Up to 3 additional topics.");
      return;
    }
    if (extraTopics.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setExtraInput("");
      return;
    }
    setExtraTopics((xs) => [...xs, t]);
    setExtraInput("");
  };

  return (
    <div className="border border-black bg-white" data-testid="refine-chat">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-black bg-[#0A0A0A] text-white">
        <Sparkles className="w-4 h-4 text-[#FFD600]" />
        <div className="ticker-label !text-white/90">Refine your news interest</div>
        {locked && <span className="ml-auto text-[10px] uppercase tracking-widest text-[#FFD600] font-bold">Locked</span>}
      </div>

      {/* Committed topics — persist across chat resets, appear above
          the live chat so users can see their running list. */}
      {committedTopics.length > 0 && (
        <div className="px-4 pt-4 pb-2 border-b border-black/10" data-testid="committed-topics">
          <div className="ticker-label !text-[10px] mb-2">Refined topics so far ({committedTopics.length})</div>
          <div className="space-y-2">
            {committedTopics.map((t, i) => (
              <div key={i} className="flex items-start gap-2 p-2 border border-black/20 bg-[#FFFBE6]">
                <span className="inline-flex w-5 h-5 flex-shrink-0 items-center justify-center bg-black text-[#FFD600] font-display text-[10px] font-black">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-wider">{t.label}</div>
                  <div className="text-[12px] italic text-neutral-600 truncate" title={t.prompt}>{t.prompt}</div>
                </div>
                <button
                  type="button"
                  onClick={() => removeCommittedTopic(i)}
                  data-testid={`remove-committed-topic-${i}`}
                  className="text-neutral-500 hover:text-red-600 flex-shrink-0"
                  aria-label={`Remove topic ${i + 1}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={scrollRef} className="max-h-[320px] overflow-y-auto px-4 py-4 space-y-4" data-testid="refine-messages">
        {history.map((h, i) => (
          <div key={i} className={`flex ${h.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`text-sm leading-relaxed px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                h.role === "user" ? "bg-[#FFD600] text-black font-medium" : "bg-[#F4F4F4] text-black"
              }`}
            >
              {h.content}
            </div>
          </div>
        ))}
        {turn && (
          <div className="flex justify-start">
            <div className="text-sm leading-relaxed px-3 py-2 max-w-[85%] bg-[#F4F4F4] text-black" data-testid="refine-latest-message">
              {turn.message}
            </div>
          </div>
        )}
        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-[#F4F4F4] text-neutral-500 text-xs inline-flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> thinking…
            </div>
          </div>
        )}
      </div>

      {/* Suggestion chips */}
      {turn?.suggestions?.length > 0 && !locked && (
        <div className="px-4 pb-3">
          <div className="ticker-label !text-[10px] mb-2">
            {turn.multi_select
              ? "Pick any that apply (or type your own below)"
              : "Tap a topic, or type your own in the box below"}
          </div>
          <div className="flex flex-wrap gap-2">
            {turn.suggestions.map((s, i) => {
              const on = selected.includes(s);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleChip(s)}
                  disabled={loading}
                  data-testid={`refine-suggestion-${i}`}
                  aria-pressed={on}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-widest font-semibold border border-black inline-flex items-center gap-1.5 transition-colors ${
                    on ? "bg-[#FFD600] text-black" : "bg-white text-black hover:bg-[#FFFBE6]"
                  }`}
                >
                  {on && <Check className="w-3 h-3" />} {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Refined prompt preview */}
      {refined && (
        <div className="mx-4 mb-3 border border-black/30 p-3 bg-[#FFFBE6]">
          <div className="ticker-label !text-[10px] mb-1">Current refined prompt</div>
          <div className="text-sm italic text-neutral-800" data-testid="refine-current-prompt">&ldquo;{refined}&rdquo;</div>
        </div>
      )}

      {/* Input row + offramp. Uses a div, not a form, because this component
          is rendered inside an outer <form>. Nested forms aren't valid HTML
          and caused the outer subscribe form to fire on Enter. */}
      {!locked && (
        <div className="border-t border-black/20">
          <div className="flex items-stretch">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (!selected.length && !input.trim()) return; // no-op on empty
                  sendCurrent();
                }
              }}
              placeholder={selected.length ? "Please tell us anything else about the specific type of content you'd like to receive (optional)" : "Type your answer…"}
              data-testid="refine-input"
              className="flex-1 px-4 py-3 text-sm outline-none bg-transparent border-0"
              disabled={loading}
            />
            <button
              type="button"
              onClick={sendCurrent}
              disabled={loading || (!selected.length && !input.trim())}
              data-testid="refine-send"
              className={`px-5 min-w-[120px] inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold disabled:opacity-40 ${
                turnCount === 0 ? "bg-[#FFD600] text-black hover:bg-[#FFC700]" : "bg-[#0A0A0A] text-white hover:bg-[#1a1a1a]"
              }`}
              aria-label={turnCount === 0 ? "Next — proceed to refinement" : "Refine further (optional)"}
              title="Enter"
            >
              {turnCount === 0 ? (
                <>Next <ArrowRight className="w-3 h-3" /></>
              ) : (
                <>Refine <ArrowRight className="w-3 h-3" /></>
              )}
            </button>
          </div>
          {turnCount > 0 && !locked && (
            <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-neutral-500 border-t border-black/10 bg-[#FAFAFA]">
              Refining further is optional — click &ldquo;Nothing else&rdquo; below when you&rsquo;re happy.
            </div>
          )}

          <div className="p-3 border-t border-black/10 flex flex-col sm:flex-row gap-2 items-stretch">
            <button
              type="button"
              onClick={lockNow}
              disabled={loading || (committedTopics.length === 0 && !refined && !selected.length && !input.trim() && history.length === 0)}
              data-testid="refine-im-done"
              className="flex-1 btn-brutal py-3 text-xs uppercase tracking-[0.14em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <CheckCircle2 className="w-4 h-4" /> Nothing else — let&rsquo;s go
            </button>
            <button
              type="button"
              onClick={startAnotherTopic}
              disabled={loading}
              data-testid="refine-add-topic-button"
              className="px-5 py-3 text-xs uppercase tracking-[0.14em] font-semibold border border-black bg-white hover:bg-[#FFD600] inline-flex items-center justify-center gap-2 disabled:opacity-40"
              title="Save this topic and refine another one from scratch"
            >
              <Plus className="w-4 h-4" /> Add another news topic
            </button>
          </div>

          {/* Up to 3 extra topics appended to the final prompt */}
          <div className="p-3 border-t border-black/10 bg-[#FAFAFA]">
            <div className="ticker-label !text-[10px] mb-2">Additional topics for your feed (optional, up to 3)</div>
            {extraTopics.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="extra-topics-list">
                {extraTopics.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-widest font-semibold border border-black bg-[#FFD600]">
                    {t}
                    <button
                      type="button"
                      onClick={() => setExtraTopics((xs) => xs.filter((_, j) => j !== i))}
                      data-testid={`remove-extra-topic-${i}`}
                      className="hover:text-red-700"
                      aria-label={`Remove ${t}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {extraTopics.length < 3 && (
              <div className="flex items-stretch">
                <input
                  ref={extraInputRef}
                  type="text"
                  value={extraInput}
                  onChange={(e) => setExtraInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addExtraTopic();
                    }
                  }}
                  placeholder="e.g. Climate news, sports scores, local tech…"
                  data-testid="extra-topic-input"
                  className="flex-1 px-3 py-2 text-sm border border-black/30 bg-white outline-none focus:bg-[#FFFBE6]"
                />
                <button
                  type="button"
                  onClick={addExtraTopic}
                  disabled={!extraInput.trim()}
                  data-testid="add-extra-topic"
                  className="px-4 bg-white border border-black border-l-0 text-[11px] uppercase tracking-widest font-semibold hover:bg-[#FFD600] disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
